import React from 'react';
import clsx from 'clsx';

import { Checkbox, Icon, Menu, Modal, Progress } from '@atom/mui';
import colors from '@atom/styles/colors';
import { UserProfile } from '@atom/types/user';
import { FILES_ENDPOINT } from '@atom/utilities/endpoints';
import { getMediaIcon } from '@atom/utilities/iconUtilities';
import textDisplayUtilities from '@atom/utilities/textDisplayUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

import RenameModal from '../../common/RenameModal';

import classes from '../../inventoryDetail/bodyPanes/folders/folders.module.css';

const { MenuItem } = Menu;

interface Props {
  media?: any[];
  userProfile: UserProfile;
  renameMedia: (id: string, name: string) => any;
  deleteMedia: (id: string) => any;
  loading?: boolean;
  toggleCheck: (id: string, checked: boolean) => any;
  selectedItems: any[];
  canUpdateMedia: boolean;
  canDeleteMedia: boolean;
}

enum ModalType {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  NONE = 'NONE',
}

const tableDisplayHeaders = ['', 'name', 'uploaded by', 'uploaded on', ''];
const tableFields = [
  'checkbox',
  'name',
  'createdBy',
  'createdDate',
  'additionalOptions',
];

class ElementMediaTable extends React.Component<Props> {
  state: {
    openModal: { type: ModalType; currentRowId: any };
  } = { openModal: { type: ModalType.NONE, currentRowId: null } };

  toggleModal = (modalToOpen: ModalType, id: any) => {
    this.setState({ openModal: { type: modalToOpen, currentRowId: id } });
  };

  buildTableHeaders = displayHeaders => {
    return displayHeaders.map((header, headerIndex) => {
      return <th key={headerIndex}>{header}</th>;
    });
  };

  buildRowFields = (row, fields) => {
    return fields.map((field, fieldIndex) => {
      return this.buildCell(row, field, fieldIndex);
    });
  };

  buildRows = (dataRows, fields) => {
    return dataRows.map((row, rowIndex) => {
      return <tr key={rowIndex}>{this.buildRowFields(row, fields)}</tr>;
    });
  };

  buildCell = (row, field, index) => {
    const {
      userProfile,
      renameMedia,
      deleteMedia,
      toggleCheck,
      selectedItems,
      canUpdateMedia,
      canDeleteMedia,
    } = this.props;

    const { type, currentRowId } = this.state.openModal;

    const isModalOpen = (modalType: ModalType) => {
      return type === modalType && currentRowId === row.id;
    };

    const onConfirmDeleteModal = () => {
      deleteMedia(row.id);
      this.toggleModal(ModalType.NONE, null);
    };

    const fieldTemplates = {
      checkbox: (
        <td key={index} className={classes.checkbox}>
          <Checkbox
            checked={selectedItems.includes(row.id)}
            onChange={() => toggleCheck(row.id, selectedItems.includes(row.id))}
          />
        </td>
      ),
      name: (
        <td key={index} className={classes.clickable}>
          <div className={classes.nameRowContainer}>
            <div className={classes.nameRowIcon}>
              {getMediaIcon(row.type, row.fileExtension, row.fileId, row.name)}
            </div>
            <a
              className={classes.clickableLink}
              href={`${FILES_ENDPOINT}/${row.fileId}`}
              target="_blank"
            >{`${row.name}.${row.fileExtension}`}</a>
          </div>
        </td>
      ),
      createdBy: (
        <td key={index}>
          {row.createdBy.firstName
            ? `${row.createdBy.firstName} ${row.createdBy.lastName}`
            : `${userProfile.firstName} ${userProfile.lastName}`}
        </td>
      ),
      createdDate: <td key={index}>{setDisplayDate(row.createdDate)}</td>,
      additionalOptions: (
        <td key={index} className={classes.additionalOptions}>
          <Menu>
            <MenuItem startAdornment={<Icon>file_download</Icon>}>
              Download
              <a
                className={classes.downloadLink}
                href={`${FILES_ENDPOINT}/${row.fileId}`}
                download={`${row.name}.${row.fileExtension}`}
              />
            </MenuItem>
            {canUpdateMedia && (
              <MenuItem
                startAdornment={<Icon>edit</Icon>}
                onClick={() => this.toggleModal(ModalType.EDIT, row.id)}
              >
                Edit
              </MenuItem>
            )}
            {canDeleteMedia && (
              <MenuItem
                startAdornment={<Icon>delete</Icon>}
                onClick={() => this.toggleModal(ModalType.DELETE, row.id)}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
          {canDeleteMedia && (
            <RenameModal
              open={isModalOpen(ModalType.EDIT)}
              closeModal={() => this.toggleModal(ModalType.NONE, null)}
              id={row.id}
              name={row.name}
              type={row.type}
              renameAction={renameMedia}
            />
          )}
          {canDeleteMedia && (
            <Modal
              open={isModalOpen(ModalType.DELETE)}
              title={`Delete ${textDisplayUtilities.capitalize(row.type)}s`}
              onCancel={() => this.toggleModal(ModalType.NONE, null)}
              confirmButtonText="Delete"
              ConfirmButtonProps={{ sx: { background: colors.brand.red } }}
              onConfirm={onConfirmDeleteModal}
            >
              {`Are you sure you want to delete this ${row.type}?`}
            </Modal>
          )}
        </td>
      ),
    };
    return fieldTemplates[field] || <td key={index}>{row[field]}</td>;
  };

  render() {
    const { media, loading } = this.props;
    return (
      <div className={clsx(classes.mainPane, classes.detail)}>
        <table className={classes.sticky}>
          <thead>
            <tr>{this.buildTableHeaders(tableDisplayHeaders)}</tr>
          </thead>
          <tbody className={classes.hidden}>
            {this.buildRows(media, tableFields)}
          </tbody>
        </table>
        {!loading ? (
          <table>
            <thead className={classes.hidden}>
              <tr>{this.buildTableHeaders(tableDisplayHeaders)}</tr>
            </thead>
            <tbody>{this.buildRows(media, tableFields)}</tbody>
          </table>
        ) : (
          <Progress />
        )}
      </div>
    );
  }
}

export default ElementMediaTable;
